import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Command as CommandPrimitive } from 'cmdk';
import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { ChevronDownIcon } from '../../../icons/chevron-down';
import { LoaderCircleIcon } from '../../../icons/loader-circle';
import { cn } from '../../../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../tooltip/tooltip';

/* -------------------------------------------------------------------------- */
/*  Command (headless list filter)                                            */
/* -------------------------------------------------------------------------- */

const Command = forwardRef<
  ElementRef<typeof CommandPrimitive>,
  ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    data-slot="command"
    className={cn(
      'flex h-full w-full flex-col overflow-hidden rounded-md bg-background',
      className
    )}
    {...props}
  />
));
Command.displayName = 'Command';

const CommandInput = forwardRef<
  ElementRef<typeof CommandPrimitive.Input>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Input> & { icon?: ReactNode }
>(({ className, icon, ...props }, ref) => (
  <div
    className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2"
    cmdk-input-wrapper=""
  >
    {icon ?? <SearchIcon className="size-3.5 shrink-0 text-muted-foreground" />}
    <CommandPrimitive.Input
      ref={ref}
      data-slot="command-input"
      className={cn(
        'min-w-0 flex-1 border-0 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground',
        className
      )}
      {...props}
    />
  </div>
));
CommandInput.displayName = 'CommandInput';

const CommandList = forwardRef<
  ElementRef<typeof CommandPrimitive.List>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    data-slot="command-list"
    className={cn('max-h-[220px] flex-1 overflow-y-auto', className)}
    {...props}
  />
));
CommandList.displayName = 'CommandList';

const CommandEmpty = forwardRef<
  ElementRef<typeof CommandPrimitive.Empty>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    data-slot="command-empty"
    className={cn('px-3 py-2.5 text-center text-sm text-muted-foreground', className)}
    {...props}
  />
));
CommandEmpty.displayName = 'CommandEmpty';

const CommandItem = forwardRef<
  ElementRef<typeof CommandPrimitive.Item>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    data-slot="command-item"
    className={cn(
      'cursor-pointer px-3 py-2.5 text-sm text-foreground transition-colors',
      'data-[selected=true]:bg-secondary data-[selected=true]:font-medium data-[selected=true]:text-primary',
      // O cmdk marca `data-disabled` mas não estiliza nada. Sem esta regra a
      // opção bloqueada fica visualmente idêntica a uma selecionável: só o
      // `aria-disabled` a distingue, e isso não alcança quem enxerga.
      'data-[disabled=true]:cursor-not-allowed data-[disabled=true]:text-muted-foreground',
      'data-[disabled=true]:data-[selected=true]:bg-transparent data-[disabled=true]:data-[selected=true]:font-normal data-[disabled=true]:data-[selected=true]:text-muted-foreground',
      className
    )}
    {...props}
  />
));
CommandItem.displayName = 'CommandItem';

const CommandLoading = forwardRef<
  ElementRef<typeof CommandPrimitive.Loading>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Loading>
>(({ className, children, ...props }, ref) => (
  <CommandPrimitive.Loading
    ref={ref}
    data-slot="command-loading"
    className={cn(
      'flex items-center justify-center gap-2 px-3 py-4 text-sm text-muted-foreground',
      className
    )}
    {...props}
  >
    {children}
  </CommandPrimitive.Loading>
));
CommandLoading.displayName = 'CommandLoading';

/* -------------------------------------------------------------------------- */
/*  Combobox                                                                  */
/* -------------------------------------------------------------------------- */

export interface ComboboxOption {
  value: string;
  label: string;
  /**
   * Opção visível mas não selecionável. O item **continua na lista**, na
   * mesma posição: sumir com ele esconderia a regra de quem está justamente
   * procurando por ele.
   */
  disabled?: boolean;
  /**
   * Por que a opção está desabilitada. Vive em três lugares: no tooltip que
   * abre no hover, no toque e quando a seta do teclado pousa na opção (RN-08
   * da EU-764); em texto só para leitor de tela (`sr-only`), ligado ao item
   * por `aria-describedby` (RN-10); e em nenhum outro — a linha fica com uma
   * só altura, igual às demais (achado do QA em 23/09).
   *
   * O teclado alcança a opção porque o combobox controla o realce do cmdk
   * (`Command value`): a navegação nativa do cmdk pula
   * `[aria-disabled="true"]`, e um usuário só de teclado nunca veria o motivo.
   */
  disabledReason?: string;
}

/**
 * - `popover` (default): button trigger + Radix Popover with a cmdk search input.
 * - `inline`: the trigger itself is the text input (Figma "combobox com busca");
 *   results drop down under the field once the query reaches `minSearchChars`.
 */
export type ComboboxSearchMode = 'popover' | 'inline';

export interface ComboboxProps {
  /**
   * Id of the focusable control (popover: trigger button; inline: the text
   * input), so a `<label htmlFor>` can point at it.
   */
  id?: string;
  options: ComboboxOption[];
  value?: string;
  onSelect: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  /**
   * Empty-state copy. Popover mode defaults to "Nenhum resultado". Inline mode
   * renders NO empty state unless this is passed explicitly (the field helper
   * text is expected to cover the "not found" case).
   */
  emptyMessage?: string;
  loading?: boolean;
  /** Loading copy (popover list) / live-region announcement (inline). */
  loadingMessage?: ReactNode;
  disabled?: boolean;
  /** Estilo de campo inválido (borda destrutiva). */
  error?: boolean;
  /**
   * Falha ao CARREGAR as opções. Estado distinto do vazio: lista vazia diz
   * "não há nada", falha de API diz "não conseguimos saber". Mostrar a lista
   * vazia nas duas faria o consultor concluir que não existe teste elegível
   * quando, na verdade, a chamada caiu.
   */
  errorMessage?: string;
  className?: string;
  /** Fires when the search/filter input changes (e.g. async option loading). */
  onSearchChange?: (query: string) => void;
  /** @default 'popover' */
  searchMode?: ComboboxSearchMode;
  /**
   * Minimum query length before `onSearchChange` fires and the list opens.
   * Ignored by popover mode. Inline mode defaults to 3; below the threshold
   * nothing is requested and nothing is shown (`onSearchChange('')` fires once
   * when the query drops back under the threshold so consumers can clear).
   */
  minSearchChars?: number;
  /**
   * Inline mode only (ignored by popover mode). Search suggestion for when
   * there is no selection yet — e.g. an item imported from a document that has
   * a name ("Puc Minas") but no id in the base. While `value` is empty, the
   * input shows this text (it counts as the current query); focusing the field
   * searches it (`onSearchChange(defaultSearch)`, when it reaches
   * `minSearchChars`) and opens the list. The panel never opens on mount.
   * Once the user types or selects an option, the suggestion is dropped.
   * Ignored when `value` already has a selection.
   */
  defaultSearch?: string;
  /**
   * Inline mode: builds the screen-reader announcement for the result count.
   * @default n => `${n} resultado(s)`
   */
  resultsAnnouncement?: (count: number) => string;
  /**
   * Root testid. Popover mode: applied to the trigger button; the search input
   * gets `<testid>-search`. Inline mode: applied to the text input
   * (`role="combobox"`); the listbox gets `<testid>-list`. Both modes give
   * each option `<testid>-item-<value>`.
   */
  'data-testid'?: string;
  /** Ligação com o campo (FormField): mensagem ativa, inválido e obrigatório. */
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-required'?: boolean;
}

interface ComboboxItemProps {
  option: ComboboxOption;
  active: boolean;
  /** Realce do cmdk (seta do teclado) está nesta opção. */
  highlighted: boolean;
  /** Id do texto de motivo, quando há um. Alvo do `aria-describedby`. */
  reasonId?: string;
  testId?: string;
  onPick: () => void;
}

/**
 * Uma linha da lista. Existe como componente próprio porque a opção
 * desabilitada precisa de estado (o tooltip) e um hook não pode viver dentro
 * de um `.map()`.
 *
 * O tooltip é CONTROLADO em vez de deixar o Radix abrir sozinho. O cmdk monta
 * o item como `<div ...props onPointerMove={disabled ? undefined : select}>`:
 * as props vêm antes, então, para um item desabilitado, ele sobrescreve com
 * `undefined` justamente o handler que o `TooltipTrigger asChild` injeta. O
 * tooltip nunca abriria. `onMouseEnter`/`onMouseLeave` o cmdk não toca.
 */
function ComboboxItem({
  option,
  active,
  highlighted,
  reasonId,
  testId,
  onPick,
}: ComboboxItemProps) {
  const [hovered, setHovered] = useState(false);
  const blocked = Boolean(option.disabled);
  const withTooltip = blocked && Boolean(reasonId);
  const rowRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);

  // O balão abre SOBRE a linha (colado ao nome, como no Figma). No instante em
  // que monta, o ponteiro parado passa a estar sobre ele: a linha recebe
  // `mouseleave`, o balão fecharia, o ponteiro voltaria à linha, reabriria —
  // um pisca-pisca de ~90 ms com o mouse parado (medido no navegador). Sair da
  // linha PARA o balão não conta como sair; sair do balão para fora, sim.
  const leavesTo = (event: MouseEvent<HTMLElement>, target: HTMLElement | null) =>
    Boolean(target && event.relatedTarget instanceof Node && target.contains(event.relatedTarget));

  // O tooltip é ancorado no TEXTO da opção (não na linha inteira): o Figma o
  // põe colado à direita do nome, dentro da lista. Ancorado na linha, que
  // ocupa toda a largura, o Radix o jogava para fora do sheet (achado do QA).
  const label = withTooltip ? (
    <TooltipTrigger asChild>
      <span>{option.label}</span>
    </TooltipTrigger>
  ) : (
    <span>{option.label}</span>
  );

  const item = (
    <CommandItem
      value={option.label}
      disabled={option.disabled}
      aria-disabled={option.disabled || undefined}
      aria-describedby={reasonId}
      onSelect={() => {
        // Clicar (ou Enter) num item desabilitado não seleciona nem fecha: o
        // cmdk já não dispara `onSelect`, mas o guard explícito sobrevive a
        // uma troca de biblioteca.
        if (blocked) return;
        onPick();
      }}
      onMouseEnter={() => blocked && setHovered(true)}
      onMouseLeave={(event) => {
        if (!blocked) return;
        if (leavesTo(event, tipRef.current?.parentElement ?? tipRef.current)) return;
        setHovered(false);
      }}
      data-active={active || undefined}
      data-testid={testId}
      ref={rowRef}
    >
      {label}
      {reasonId ? (
        // Só para o leitor de tela: visualmente o motivo vive no tooltip. Em
        // texto corrido a linha ficava com duas alturas e destoava da lista.
        <span id={reasonId} className="sr-only">
          {option.disabledReason}
        </span>
      ) : null}
    </CommandItem>
  );

  if (!withTooltip) return item;

  return (
    // `TooltipProvider` local: o combobox não pode depender de o app ter posto
    // um provider na raiz — sem ele o Radix lança, e a falha apareceria só na
    // tela que usa opção desabilitada. Provider aninhado é seguro.
    //
    // Controlado (`open`): o cmdk monta o item como
    // `<div ...props onPointerMove={disabled ? undefined : select}>` e apaga o
    // handler que o Radix injetaria; e o realce por teclado não é hover. Os
    // dois estados que abrem o tooltip vêm de fora do Radix.
    <TooltipProvider delayDuration={150}>
      <Tooltip open={hovered || highlighted}>
        {item}
        <TooltipContent
          ref={tipRef}
          side="right"
          sideOffset={8}
          // Figma (Jobs 18239-40000, QA EU-764 VIS-004): o motivo do bloqueio é
          // um balão CLARO — cinza (muted) com texto escuro —, não o tooltip
          // escuro padrão (popover). É uma dica ao lado de uma lista também
          // clara, e o contraste do escuro chamava mais atenção que a opção.
          className="bg-muted text-foreground shadow-sm"
          onMouseLeave={(event) => {
            if (leavesTo(event, rowRef.current)) return;
            setHovered(false);
          }}
          data-testid={testId ? `${testId}-tooltip` : undefined}
        >
          {option.disabledReason}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Navegação por seta que NÃO pula as opções desabilitadas.
 *
 * O cmdk percorre `[cmdk-item]:not([aria-disabled="true"])`, então uma opção
 * bloqueada nunca recebe o realce — e quem usa só teclado nunca vê o motivo
 * (MAP-056). Com o realce controlado (`Command value`), o combobox decide a
 * ordem: todos os itens renderizados, na ordem em que o cmdk os deixou (ele
 * reordena o DOM pelo score da busca). Só entra em ação quando há opção
 * bloqueada; sem ela, a navegação nativa fica intacta.
 *
 * Enter numa opção bloqueada não seleciona: o cmdk despacha o evento de
 * seleção para o item realçado, e o item desabilitado não escuta.
 *
 * Limite herdado do cmdk: o realce é endereçado pelo VALOR do item (o
 * `label`). Duas opções com o mesmo rótulo são, para o cmdk, o mesmo item —
 * a seta não distingue uma da outra (nem na navegação nativa). Rótulos
 * únicos por lista são pré-requisito do componente, não desta função.
 */
function moveHighlightThroughAll(list: HTMLElement | null, direction: 1 | -1): string | undefined {
  const items = Array.from(list?.querySelectorAll<HTMLElement>('[cmdk-item]') ?? []);
  if (items.length === 0) return undefined;
  const current = items.findIndex((el) => el.getAttribute('aria-selected') === 'true');
  const next =
    current === -1
      ? direction === 1
        ? 0
        : items.length - 1
      : Math.min(items.length - 1, Math.max(0, current + direction));
  const target = items[next];
  target?.scrollIntoView?.({ block: 'nearest' });
  return target?.getAttribute('data-value') ?? undefined;
}

function Combobox(props: ComboboxProps) {
  if (props.searchMode === 'inline') return <InlineCombobox {...props} />;
  return <PopoverCombobox {...props} />;
}
Combobox.displayName = 'Combobox';

/* ----------------------------- popover mode ------------------------------ */

function PopoverCombobox({
  id,
  options,
  value,
  onSelect,
  placeholder = 'Selecione...',
  searchPlaceholder = 'Buscar...',
  emptyMessage = 'Nenhum resultado',
  loading = false,
  loadingMessage,
  disabled = false,
  error = false,
  errorMessage,
  className,
  onSearchChange,
  'data-testid': testId,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  // Base estável para os ids de motivo — dois comboboxes na mesma tela não
  // podem produzir o mesmo `aria-describedby`.
  const listboxId = useId();

  // Realce do cmdk, controlado só quando há opção bloqueada (ver
  // `moveHighlightThroughAll`). `undefined` devolve o controle ao cmdk.
  const hasBlocked = options.some((o) => o.disabled);
  const [highlighted, setHighlighted] = useState<string | undefined>(undefined);
  const listRef = useRef<HTMLDivElement>(null);
  const onKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!hasBlocked) return;
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const next = moveHighlightThroughAll(listRef.current, event.key === 'ArrowDown' ? 1 : -1);
    if (next === undefined) return;
    // Captura + stopPropagation: o evento morre antes de chegar ao handler do
    // cmdk (que pularia a opção bloqueada) e ao input (que moveria o caret).
    event.preventDefault();
    event.stopPropagation();
    setHighlighted(next);
  };

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) onSearchChange?.('');
      }}
    >
      <PopoverPrimitive.Trigger asChild disabled={disabled}>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid || undefined}
          aria-required={ariaRequired || undefined}
          data-slot="combobox-trigger"
          data-testid={testId}
          className={cn(
            'flex min-h-9 w-full cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 py-2 shadow-2xs transition-colors',
            'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-primary',
            // Campo com erro (Figma 20080:1334, mesmo padrão do Input): borda destructive +
            // anel de 3px destructive-60 sempre visível — por `error` ou só pelo aria-invalid
            // que o FormFieldControl passa.
            'aria-invalid:border-destructive aria-invalid:shadow-none aria-invalid:ring-[3px] aria-invalid:ring-destructive-60',
            error &&
              'border-destructive shadow-none ring-[3px] ring-destructive-60 focus-visible:outline-destructive',
            disabled && 'cursor-not-allowed opacity-50',
            className
          )}
        >
          <span
            className={cn(
              'flex-1 truncate text-sm leading-5 text-left',
              selected ? 'text-foreground' : 'text-muted-foreground'
            )}
          >
            {selected?.label ?? placeholder}
          </span>
          <ChevronIcon
            className={cn(
              'size-4 shrink-0 text-muted-foreground transition-transform',
              open && 'rotate-180'
            )}
          />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          data-slot="combobox-content"
          className="z-50 w-[var(--radix-popover-trigger-width)] rounded-md border border-border bg-background shadow-md"
          sideOffset={4}
          align="start"
          // Dentro de um Dialog (modal), o `react-remove-scroll` do Radix bloqueia
          // o wheel/touch no conteúdo portalizado do Popover. Parar a propagação
          // impede que o listener de scroll-lock (no document) capture o evento,
          // então o scroll NATIVO da lista (`overflow-y-auto`) volta a funcionar.
          // Também evita scroll-chaining pra página atrás em uso normal.
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onKeyDownCapture={onKeyDownCapture}
        >
          <Command
            {...(hasBlocked
              ? { value: highlighted ?? '', onValueChange: (v: string) => setHighlighted(v) }
              : {})}
          >
            <CommandInput
              placeholder={searchPlaceholder}
              onValueChange={(q) => onSearchChange?.(q)}
              data-testid={testId ? `${testId}-search` : undefined}
            />
            <CommandList ref={listRef}>
              {errorMessage ? (
                <div
                  role="alert"
                  className="px-3 py-6 text-center text-sm text-destructive"
                  data-testid={testId ? `${testId}-error` : undefined}
                >
                  {errorMessage}
                </div>
              ) : null}
              {!errorMessage && loading && (
                <CommandLoading>{loadingMessage ?? 'Carregando...'}</CommandLoading>
              )}
              {!errorMessage && <CommandEmpty>{emptyMessage}</CommandEmpty>}
              {(errorMessage ? [] : options).map((option) => (
                <ComboboxItem
                  key={option.value}
                  option={option}
                  active={option.value === value}
                  highlighted={hasBlocked && highlighted === option.label}
                  reasonId={
                    option.disabled && option.disabledReason
                      ? `${listboxId}-reason-${option.value}`
                      : undefined
                  }
                  testId={testId ? `${testId}-item-${option.value}` : undefined}
                  onPick={() => {
                    onSelect(option.value);
                    setOpen(false);
                  }}
                />
              ))}
            </CommandList>
          </Command>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
PopoverCombobox.displayName = 'PopoverCombobox';

/* ------------------------------ inline mode ------------------------------ */

const INLINE_DEFAULT_MIN_SEARCH_CHARS = 3;

function defaultResultsAnnouncement(count: number): string {
  return count === 1 ? '1 resultado' : `${count} resultados`;
}

/**
 * Figma "combobox com busca": the field IS the search box. Focus clears the
 * visible text (placeholder shows), typing under `minSearchChars` requests and
 * shows nothing, reaching it calls `onSearchChange` and drops a listbox (4 rows
 * of 44px visible, scroll for the rest) right under the frame. Loading swaps the
 * chevron for a spinner; a polite live region announces loading / result count.
 */
function InlineCombobox({
  id,
  options,
  value,
  onSelect,
  placeholder = 'Selecione...',
  emptyMessage,
  loading = false,
  loadingMessage,
  disabled = false,
  error = false,
  className,
  onSearchChange,
  minSearchChars = INLINE_DEFAULT_MIN_SEARCH_CHARS,
  defaultSearch,
  resultsAnnouncement = defaultResultsAnnouncement,
  'data-testid': testId,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
}: ComboboxProps) {
  const listId = `${useId()}-list`;
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimerRef = useRef<number | null>(null);
  // `true` once consumers have been told to clear (`onSearchChange('')`) for
  // the current sub-threshold stretch, so that fires at most once per drop.
  const clearedRef = useRef(true);

  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  // `defaultSearch` stays in play until the user types or selects something.
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);

  const selected = options.find((o) => o.value === value);
  const suggestion = !value && !suggestionDismissed && defaultSearch ? defaultSearch : undefined;
  const reachedThreshold = query.length >= minSearchChars;
  const showEmpty = emptyMessage != null && !loading && options.length === 0;
  const listVisible = open && reachedThreshold && (options.length > 0 || showEmpty);
  const safeActiveIndex = activeIndex < options.length ? activeIndex : -1;
  const activeOption = listVisible && safeActiveIndex >= 0 ? options[safeActiveIndex] : undefined;
  const activeOptionId = activeOption ? `${listId}-opt-${safeActiveIndex}` : undefined;

  const announcement = loading
    ? (loadingMessage ?? 'Carregando…')
    : open && reachedThreshold
      ? resultsAnnouncement(options.length)
      : null;

  useEffect(
    () => () => {
      if (blurTimerRef.current != null) window.clearTimeout(blurTimerRef.current);
    },
    []
  );

  function cancelPendingBlur() {
    if (blurTimerRef.current != null) {
      window.clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
  }

  function close() {
    setOpen(false);
    setActiveIndex(-1);
  }

  function handleFocus() {
    cancelPendingBlur();
    setFocused(true);
    setActiveIndex(-1);
    clearedRef.current = true;
    if (suggestion) {
      setQuery(suggestion);
      if (suggestion.length >= minSearchChars) {
        clearedRef.current = false;
        onSearchChange?.(suggestion);
        setOpen(true);
      }
      return;
    }
    setQuery('');
    if (minSearchChars === 0) {
      onSearchChange?.('');
      setOpen(true);
    }
  }

  function handleBlur() {
    // Deferred a tick so an option's mousedown (which prevents default and
    // therefore never blurs) and consumer focus juggling can't race the close.
    cancelPendingBlur();
    blurTimerRef.current = window.setTimeout(() => {
      blurTimerRef.current = null;
      setFocused(false);
      close();
    }, 0);
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const q = event.target.value;
    setQuery(q);
    setSuggestionDismissed(true);
    setActiveIndex(-1);
    if (q.length >= minSearchChars) {
      clearedRef.current = false;
      onSearchChange?.(q);
      setOpen(true);
      return;
    }
    if (!clearedRef.current) {
      clearedRef.current = true;
      onSearchChange?.('');
    }
    setOpen(false);
  }

  function select(option: ComboboxOption) {
    onSelect(option.value);
    setQuery(option.label);
    setSuggestionDismissed(true);
    close();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        if (!listVisible) {
          if (reachedThreshold) setOpen(true);
          return;
        }
        if (options.length === 0) return;
        setActiveIndex((i) => (i + 1) % options.length);
        return;
      }
      case 'ArrowUp': {
        event.preventDefault();
        if (!listVisible || options.length === 0) return;
        setActiveIndex((i) => (i <= 0 ? options.length - 1 : i - 1));
        return;
      }
      case 'Enter': {
        if (!listVisible) return;
        event.preventDefault();
        if (activeOption) select(activeOption);
        return;
      }
      case 'Escape': {
        if (!open) return;
        event.preventDefault();
        close();
        return;
      }
      default:
    }
  }

  function handleFrameMouseDown(event: MouseEvent<HTMLDivElement>) {
    // Clicks on the padding, the chevron or the list's scrollbar must not
    // steal focus from the input (which would close the list).
    if (disabled || event.target === inputRef.current) return;
    event.preventDefault();
    inputRef.current?.focus();
  }

  function handleToggleMouseDown(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
  }

  function handleToggleClick() {
    if (disabled) return;
    const input = inputRef.current;
    if (!focused) {
      input?.focus();
      return;
    }
    if (open) {
      close();
      return;
    }
    if (reachedThreshold) {
      setOpen(true);
      return;
    }
    input?.blur();
  }

  return (
    <div
      data-slot="combobox-inline"
      data-state={listVisible ? 'open' : 'closed'}
      onMouseDown={handleFrameMouseDown}
      className={cn(
        'relative flex min-h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-2 shadow-2xs transition-colors',
        'focus-within:outline focus-within:outline-2 focus-within:-outline-offset-1 focus-within:outline-primary',
        error && 'border-destructive focus-within:outline-destructive',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
    >
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-expanded={listVisible}
        aria-controls={listVisible ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={activeOptionId}
        aria-busy={loading || undefined}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid || undefined}
        aria-required={ariaRequired || undefined}
        autoComplete="off"
        disabled={disabled}
        placeholder={placeholder}
        value={focused ? query : (selected?.label ?? suggestion ?? '')}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        data-testid={testId}
        className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm leading-5 text-foreground outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
      />
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        disabled={disabled}
        onMouseDown={handleToggleMouseDown}
        onClick={handleToggleClick}
        data-slot="combobox-inline-toggle"
        className="flex shrink-0 items-center justify-center text-muted-foreground outline-none disabled:cursor-not-allowed"
      >
        {loading ? (
          <LoaderCircleIcon data-slot="combobox-inline-spinner" className="animate-spin" />
        ) : (
          <ChevronDownIcon className={cn('transition-transform', listVisible && 'rotate-180')} />
        )}
      </button>
      {listVisible && (
        <ul
          id={listId}
          role="listbox"
          data-slot="combobox-inline-list"
          data-testid={testId ? `${testId}-list` : undefined}
          // 4 rows of 44px + 8px padding + a 4px peek of the 5th row → scroll cue.
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[188px] overflow-y-auto rounded-md border border-border bg-background p-1 shadow-md"
        >
          {options.map((option, index) => {
            const active = index === safeActiveIndex;
            return (
              <li
                key={option.value}
                id={`${listId}-opt-${index}`}
                role="option"
                aria-selected={active}
                data-active={active || undefined}
                data-selected={option.value === value || undefined}
                data-testid={testId ? `${testId}-item-${option.value}` : undefined}
                onMouseDown={(event) => {
                  event.preventDefault();
                  select(option);
                }}
                onMouseMove={() => {
                  if (!active) setActiveIndex(index);
                }}
                className={cn(
                  // EU-761 RN-46: 44px touch target (12 + 20 + 12). Block (not
                  // flex) so `truncate` still ellipsizes the label.
                  'min-h-11 cursor-pointer truncate rounded-sm px-2 py-3 text-sm leading-5 text-foreground',
                  active && 'bg-secondary font-medium text-primary'
                )}
              >
                {option.label}
              </li>
            );
          })}
          {showEmpty && (
            <li
              role="presentation"
              data-slot="combobox-inline-empty"
              className="px-2 py-1.5 text-center text-sm text-muted-foreground"
            >
              {emptyMessage}
            </li>
          )}
        </ul>
      )}
      <span role="status" aria-live="polite" className="sr-only">
        {announcement}
      </span>
    </div>
  );
}
InlineCombobox.displayName = 'InlineCombobox';

/* -------------------------------------------------------------------------- */
/*  Internal icons                                                            */
/* -------------------------------------------------------------------------- */

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export { Combobox, Command, CommandEmpty, CommandInput, CommandItem, CommandList, CommandLoading };
